import { type Ari, createAri } from '@atlassian/jira-platform-ari';
import type { mainAGSQuery$variables } from '@atlassian/jira-relay/src/__generated__/mainAGSQuery.graphql';
import type { mainDeploymentsAGSQuery$data } from '@atlassian/jira-relay/src/__generated__/mainDeploymentsAGSQuery.graphql';
import type {
	mainDeploymentsQuery$data,
	mainDeploymentsQuery$variables,
} from '@atlassian/jira-relay/src/__generated__/mainDeploymentsQuery.graphql';
import type {
	mainIssueQuery$data,
	mainIssueQuery$variables,
} from '@atlassian/jira-relay/src/__generated__/mainIssueQuery.graphql';
import type { CloudId, IssueId, IssueTypeId } from '@atlassian/jira-shared-types/src/general.tsx';
import { UNASSIGNED_EPIC_ID } from '../../../common/constants';
import {
	type Deployment,
	type DeploymentEnvironmentType,
	DeploymentEnvironmentTypeOptions,
} from '../../../common/types';
import { DEPLOYMENTS_PAGE_SIZE, ISSUE_PAGE_SIZE } from '../constants';
import type { DeploymentFilter, IssueFilter } from '../types';

type DevOpsEnvironmentCategory = 'DEVELOPMENT' | 'PRODUCTION' | 'STAGING' | 'TESTING' | 'UNMAPPED';

export const transformEnvironmentType = (
	type: DevOpsEnvironmentCategory | string,
): DeploymentEnvironmentType => {
	switch (type) {
		case DeploymentEnvironmentTypeOptions.PRODUCTION:
			return 'PRODUCTION';
		case DeploymentEnvironmentTypeOptions.STAGING:
			return 'STAGING';
		case DeploymentEnvironmentTypeOptions.TESTING:
			return 'TESTING';
		case DeploymentEnvironmentTypeOptions.DEVELOPMENT:
			return 'DEVELOPMENT';
		case DeploymentEnvironmentTypeOptions.UNMAPPED:
			return 'UNMAPPED';
		default:
			throw new Error(`Unhandled environment type: ${type}`);
	}
};

export const getDeploymentQueryVariables = (
	{ issueIds, environments, timeWindow }: DeploymentFilter,
	cloudId: CloudId,
	after: string | null = null,
): mainDeploymentsQuery$variables => ({
	first: DEPLOYMENTS_PAGE_SIZE,
	after,
	filter: {
		issueIds: issueIds.map((id) =>
			createAri({
				resourceOwner: 'jira',
				cloudId,
				resourceType: 'issue',
				resourceId: id,
			}),
		),
		environmentCategories:
			environments.types.length > 0 ? environments.types.map(transformEnvironmentType) : null,
		environmentDisplayNames: environments.names.length > 0 ? environments.names : null,
		timeWindow: {
			after: timeWindow.after.toISOString(),
			before: timeWindow.before.toISOString(),
		},
	},
});

export const getAGSDeploymentQueryVariables = (
	{ issueIds, environments, timeWindow }: DeploymentFilter,
	cloudId: CloudId,
	after: string | null = null,
): mainAGSQuery$variables => ({
	first: DEPLOYMENTS_PAGE_SIZE,
	after,
	filter: {
		and: [
			{
				to_environmentType: {
					is: environments.categories?.length
						? environments.categories.map(transformEnvironmentType)
						: undefined,
				},
				createdAt: {
					after: timeWindow.after.toISOString(),
					before: timeWindow.before.toISOString(),
				},
			},
		],
	},
	ids: issueIds.map((id) =>
		createAri({
			resourceOwner: 'jira',
			cloudId,
			resourceType: 'issue',
			resourceId: id,
		}),
	),
});

const transformEpicIds = (
	cloudId: CloudId,
	epicIds: (IssueId | typeof UNASSIGNED_EPIC_ID)[],
): (Ari | null)[] | null => {
	if (epicIds.length === 0) {
		// Meaning "do not apply filter"
		return null;
	}
	return epicIds.map((id) => {
		if (id === UNASSIGNED_EPIC_ID) {
			// Meaning "include issues that aren't in epics"
			return null;
		}
		return createAri({
			resourceOwner: 'jira',
			cloudId,
			resourceType: 'issue',
			resourceId: id,
		});
	});
};

const transformIssueTypeIds = (cloudId: CloudId, issueTypeIds: IssueTypeId[]): Ari[] | null => {
	if (issueTypeIds.length === 0) {
		// Meaning "do not apply filter"
		return null;
	}
	return issueTypeIds.map((id) =>
		createAri({
			resourceOwner: 'jira',
			cloudId,
			resourceType: 'issue-type',
			resourceId: id,
		}),
	);
};

export const getIssueQueryVariables = (
	{
		projectId,
		text,
		assignees,
		environments,
		timeWindow,
		epicIds,
		issueTypeIds,
		fixVersions,
		showIssuesWithoutDeployments,
	}: IssueFilter,
	cloudId: CloudId,
	after: string | null,
): mainIssueQuery$variables => ({
	first: ISSUE_PAGE_SIZE,
	after,
	filter: {
		projectId: createAri({
			resourceOwner: 'jira',
			cloudId,
			resourceType: 'project',
			resourceId: projectId,
		}),
		text,
		assignees: assignees.length > 0 ? assignees : null,
		environmentCategories:
			environments.types.length > 0 ? environments.types.map(transformEnvironmentType) : null,
		environmentDisplayNames: environments.names.length > 0 ? environments.names : null,
		releaseStatusFilter: showIssuesWithoutDeployments ? 'RELEASED_AND_UNRELEASED' : 'RELEASED',
		timeWindow: {
			after: timeWindow.after.toISOString(),
			before: timeWindow.before.toISOString(),
		},
		epicIds: transformEpicIds(cloudId, epicIds),
		issueTypes: transformIssueTypeIds(cloudId, issueTypeIds),
		fixVersions: fixVersions.length > 0 ? fixVersions : null,
	},
});

export const getNextDeploymentPageCursor = (
	queryData?: mainDeploymentsQuery$data,
): string | null => {
	const pageInfo = queryData?.jiraReleases?.deployments?.pageInfo;
	if (
		pageInfo == null ||
		pageInfo.hasNextPage === false ||
		queryData?.jiraReleases?.deployments?.edges == null
	) {
		return null;
	}
	const { edges = [] } = queryData.jiraReleases.deployments;
	const lastEdge = edges[edges.length - 1];
	return lastEdge == null ? null : lastEdge.cursor;
};

export const getNextDeploymentAGSPageCursor = (
	queryData?: mainDeploymentsAGSQuery$data,
): string | null => {
	const pageInfo = queryData?.graphStore?.projectAssociatedDeployment?.pageInfo;
	if (pageInfo == null || pageInfo.hasNextPage === false || pageInfo.endCursor == null) {
		return null;
	}
	return pageInfo.endCursor;
};

export const getNextIssuePageCursor = (queryData?: mainIssueQuery$data): string | null => {
	const pageInfo = queryData?.jiraReleases?.issues?.pageInfo;

	if (
		pageInfo == null ||
		pageInfo.hasNextPage === false ||
		queryData?.jiraReleases?.issues?.edges == null
	) {
		return null;
	}
	const { edges = [] } = queryData.jiraReleases.issues;
	const lastEdge = edges[edges.length - 1];
	return lastEdge == null ? null : lastEdge.cursor;
};

export const groupDeploymentsByIssueId = (group: Deployment[]) => {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const map: Record<string, any> = {};
	group.forEach((value) => {
		value.issueIds.forEach((key) => {
			const existing = map[String(key)] || [];
			map[String(key)] = [...existing, value];
		});
	});
	return map;
};
